import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ElementRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { ProtocolSettingsComponent } from "../ProtocolSettingsComponent";
import { HttpSettings } from 'DiscoveryEngine.TS/Protocols';
import { IpScope } from 'DiscoveryEngine.TS/Core';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';

@Component({
	selector: 'http-settings',
	templateUrl: './http-settings.component.html',
	styleUrls: ['./http-settings.component.scss']
})
export class HttpSettingsComponent extends ProtocolSettingsComponent<HttpSettings> implements OnChanges {

	public Name: UntypedFormControl;
	public Username: UntypedFormControl;
	public Password: UntypedFormControl;
	public PasswordConfirm: UntypedFormControl;
	public ShowChars: UntypedFormControl;
	public TargetServer: UntypedFormControl;
	public OrganizationFilter: UntypedFormControl;
	public SiteNetworkFilter: UntypedFormControl;
	public Type: UntypedFormControl;
	public IpScope: UntypedFormControl;
	public Port: UntypedFormControl;
	public LoginTimeout: UntypedFormControl;
	public ReadTimeout: UntypedFormControl;
	public ReadPause: UntypedFormControl;
	public UseHttps: UntypedFormControl;
	public IsDefault: UntypedFormControl;

	private PasswordChanged: boolean;

	constructor(private fb: UntypedFormBuilder) {
		super();
		this.BuildForm();
	}

	protected BuildForm(): void {
		this.PasswordInputType = "password";

		if (this.Settings) {
			let ipScope = this.Settings.IpScope.ToString();

			let type: string = "";
			if (this.Settings.IsMerakiApiKey)
				type = "Meraki Key";
			else if (this.Settings.IsUniFiKey)
				type = "UniFi Key";
			else if (this.Settings.IsRuckusKey)
				type = "Ruckus Key";
			else if (this.Settings.IsMistKey)
				type = "Juniper MIST Key";
			else if (this.Settings.IsExtremeIQKey)
				type = "ExtremeIQ Key";
			else if (this.Settings.IsArubaInstantOnKey)
				type = "Aruba InstantOn Key";

			this.Name = new UntypedFormControl(this.Settings.Name, Validators.required);
			this.Username = new UntypedFormControl(this.Settings.UserName, Validators.required);
			this.Password = new UntypedFormControl(this.hidePassword(this.Settings.Password));
			this.PasswordConfirm = new UntypedFormControl(this.hidePassword(this.Settings.Password));
			this.ShowChars = new UntypedFormControl(false);
			this.TargetServer = new UntypedFormControl(this.Settings.TargetServer);
			this.OrganizationFilter = new UntypedFormControl(this.Settings.OrganizationFilter);
			this.SiteNetworkFilter = new UntypedFormControl(this.Settings.SiteNetworkFilter);
			this.Type = new UntypedFormControl(type);
			this.IpScope = new UntypedFormControl(ipScope);
			this.Port = new UntypedFormControl(this.Settings.Port, Validators.compose([Validators.required, this.minValue(1), this.maxValue(65535)]));
			this.LoginTimeout = new UntypedFormControl(this.Settings.LoginTimeout, Validators.compose([Validators.required, this.minValue(0), this.maxValue(120000)]));
			this.ReadTimeout = new UntypedFormControl(this.Settings.ReadTimeout, Validators.compose([Validators.required, this.minValue(0), this.maxValue(120000)]));
			this.ReadPause = new UntypedFormControl(this.Settings.ReadPause, Validators.compose([Validators.required, this.minValue(0), this.maxValue(120000)]));
			this.UseHttps = new UntypedFormControl(this.Settings.UseHTTPS);
			this.IsDefault = new UntypedFormControl(this.Settings.IsDefault);

			this.enableDisableControls();

			this.PasswordChanged = false;

			this.SettingsForm = this.fb.group({
				Name: this.Name,
				Username: this.Username,
				Password: this.Password,
				PasswordConfirm: this.PasswordConfirm,
				ShowChars: this.ShowChars,
				TargetServer: this.TargetServer,
				OrganizationFilter: this.OrganizationFilter,
				SiteNetworkFilter: this.SiteNetworkFilter,
				Type: this.Type,
				IpScope: this.IpScope,
				Port: this.Port,
				LoginTimeout: this.LoginTimeout,
				ReadTimeout: this.ReadTimeout,
				ReadPause: this.ReadPause,
				UseHttps: this.UseHttps,
				IsDefault: this.IsDefault,
			},
				{
					validator: Validators.compose([
						this.equalTo(this.Password, this.PasswordConfirm, "passwordsEqual")
					])
				});
		}
		else {
			this.SettingsForm = null;
			this.Name = null;
			this.Username = null;
			this.Password = null;
			this.PasswordConfirm = null;
			this.ShowChars = null;
			this.TargetServer = null;
			this.OrganizationFilter = null;
			this.SiteNetworkFilter = null;
			this.Type = null;
			this.IpScope = null;
			this.Port = null;
			this.LoginTimeout = null;
			this.ReadTimeout = null;
			this.ReadPause = null;
			this.UseHttps = null;
			this.IsDefault = null;
		}
	}

	public enableDisableControls(): void {
		this.enableDisableFormControl(this.ShowChars, this.AllowShowChars);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.BuildForm();
	}

	public get invalidTempChanges(): string[] {
		return ["HTTP"];
	}

	public applyTempChanges(): void {
		this.Settings.Name = this.Name.value;
		this.Settings.UserName = this.Username.value;
		if (this.PasswordChanged) {
			this.Settings.Password = StringUtils.ObfuscateString2(this.Password.value);
		}
		this.Settings.TargetServer = this.TargetServer.value;
		this.Settings.OrganizationFilter = this.OrganizationFilter.value;
		this.Settings.SiteNetworkFilter = this.SiteNetworkFilter.value;
		this.Settings.IsMerakiKey = (this.Type.value === "Meraki Key");
		this.Settings.IsUniFiKey = (this.Type.value === "UniFi Key");
		this.Settings.IsRuckusKey = (this.Type.value === "Ruckus Key");
		this.Settings.IsMistKey = (this.Type.value === "Juniper MIST Key");
		this.Settings.IsExtremeIQKey = (this.Type.value === "ExtremeIQ Key");
		this.Settings.IsArubaInstantOnKey = (this.Type.value === "Aruba InstantOn Key");
		this.SaveScope(this.Settings.IpScope, this.IpScope.value);
		this.Settings.Port = parseInt(this.Port.value);
		this.Settings.LoginTimeout = parseInt(this.LoginTimeout.value);
		this.Settings.ReadTimeout = parseInt(this.ReadTimeout.value);
		this.Settings.ReadPause = parseInt(this.ReadPause.value);
		this.Settings.UseHTTPS = this.UseHttps.value;
		this.Settings.IsDefault = this.IsDefault.value;

		this.BuildForm();
		this.OnApplyTempChanges();
	}

	private SaveScope(scope: IpScope, str: string): void {
		scope.FromString(str);
	}

}
